import React, { useCallback } from "react";
import PropTypes from "prop-types";

/**
 * TypewriterAnimation
 *
 * Initializes the CSS typewriter animation on the nearest <a> ancestor by
 * setting the --n CSS custom property to the character count of the animated
 * text. The animation itself lives in _typography.scss.
 *
 * ─── HOW THE ANIMATION WORKS ────────────────────────────────────────────────
 *
 * Elements with class `action-element` or `typewriter-anchor` get a two-phase
 * hover animation driven by the --n variable:
 *
 *   1. Delete phase  — text erases right-to-left (fast, ~32ms × sqrt(n))
 *   2. Reveal phase  — text types left-to-right (slower, ~190ms × sqrt(n))
 *
 * Duration scales with sqrt(n) so longer labels animate faster per character,
 * keeping the total time roughly constant across different label lengths.
 *
 * ─── BASIC USAGE ────────────────────────────────────────────────────────────
 *
 * Wrap the text content inside any element that carries `action-element`:
 *
 *   <a className="action-element">
 *     <TypewriterAnimation>Label text</TypewriterAnimation>
 *   </a>
 *
 * The component sets --n on the nearest <a> ancestor, so it works regardless
 * of how deeply nested it is:
 *
 *   <a className="action-element">
 *     <span>
 *       <TypewriterAnimation>Label text</TypewriterAnimation>
 *     </span>
 *   </a>
 *
 * ─── USING hoverAnimation ON THE LINK COMPONENT ─────────────────────────────
 *
 * The <Link> component (general-components/link/link.jsx) accepts a
 * `hoverAnimation` prop that automatically adds `action-element` and wraps
 * the content in <TypewriterAnimation>:
 *
 *   <Link link={someLink} hoverAnimation />
 *
 * ─── typewriter-anchor — MEASURING A SUBSET OF THE LINK TEXT ────────────────
 *
 * By default --n is measured from the full text content of the <a>. If the
 * link contains extra elements (icons, dates, metadata) that should not count
 * toward the animation length, add `typewriter-anchor` to the wrapper around
 * just the animated text:
 *
 *   <a className="action-element">
 *     <img src={icon} />
 *     <span className="typewriter-anchor">
 *       <TypewriterAnimation>Label text</TypewriterAnimation>
 *     </span>
 *   </a>
 *
 * The component walks up the DOM and uses the first `typewriter-anchor`
 * ancestor's text length instead of the full <a> text.
 *
 * When `typewriter-anchor` is present, the CSS animation is applied to it
 * directly and the parent `action-element` is excluded via :has() in SCSS:
 *
 *   .action-element:not(:has(.typewriter-anchor)) — animates when no anchor child
 *   .typewriter-anchor                            — always animates
 *
 * ─── NOTES ──────────────────────────────────────────────────────────────────
 *
 * - The hidden <span> is a DOM anchor for the callback ref; it has no visual
 *   effect (display: none, aria-hidden).
 * - The callback ref fires after React commits the DOM, so async/data-driven
 *   content is always present when --n is computed.
 * - If no <a> ancestor is found the component is a safe no-op.
 */

const TypewriterAnimation = ({ children }) => {
  const refCallback = useCallback((node) => {
    if (!node) return;
    let el = node.parentElement;
    let anchorEl = null;
    while (el && el.tagName !== "A") {
      if (el.classList?.contains("typewriter-anchor")) anchorEl = el;
      el = el.parentElement;
    }
    if (el) {
      const source = anchorEl || el;
      el.style.setProperty("--n", source.textContent.trim().length);
    }
  }, []);

  return (
    <>
      <span ref={refCallback} style={{ display: "none" }} aria-hidden="true" />
      {children}
    </>
  );
};

TypewriterAnimation.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TypewriterAnimation;
